import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Plus, Trash2, AlertTriangle } from 'lucide-react';
import {
  useAssignmentsQuery,
  useCreateAssignment,
  useUpdateAssignment,
  useDeleteAssignment,
  useProjectMembersQuery,
} from '@/hooks/useAssignments';

interface Task {
  id: string;
  sheetId: string;
  projectId: string;
  name: string;
  duration: number;
}

interface TaskAssignmentsProps {
  task: Task;
  sheetId: string;
}

export function TaskAssignments({ task, sheetId }: TaskAssignmentsProps) {
  const { data: assignments, isLoading: assignmentsLoading } = useAssignmentsQuery(task.id);
  const { data: projectMembers, isLoading: membersLoading } = useProjectMembersQuery(task.projectId);

  const createAssignment = useCreateAssignment(task.id, sheetId);
  const updateAssignment = useUpdateAssignment(task.id, sheetId);
  const deleteAssignment = useDeleteAssignment(task.id, sheetId);

  const [isAddingAssignment, setIsAddingAssignment] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [units, setUnits] = useState('100');
  const [work, setWork] = useState('0');

  // Filter out users that are already assigned
  const availableMembers = projectMembers?.filter(
    (member) => !assignments?.some((a) => a.userId === member.user.id)
  );

  // Check for overallocation (total units > 100% for any user)
  const totalUnits = assignments?.reduce((sum, a) => sum + a.units, 0) || 0;
  const isOverallocated = totalUnits > 100;

  const handleAddAssignment = useCallback(async () => {
    if (!selectedUserId) return;

    try {
      await createAssignment.mutateAsync({
        userId: selectedUserId,
        units: parseInt(units, 10) || 100,
        work: parseFloat(work) || 0,
        workUnit: 'h',
      });

      setIsAddingAssignment(false);
      setSelectedUserId('');
      setUnits('100');
      setWork('0');
    } catch (error) {
      console.error('Failed to add assignment:', error);
    }
  }, [selectedUserId, units, work, createAssignment]);

  const handleUpdateUnits = useCallback(
    (assignmentId: string, newUnits: number) => {
      updateAssignment.mutate({
        assignmentId,
        updates: { units: newUnits },
      });
    },
    [updateAssignment]
  );

  const handleUpdateWork = useCallback(
    (assignmentId: string, newWork: number) => {
      updateAssignment.mutate({
        assignmentId,
        updates: { work: newWork },
      });
    },
    [updateAssignment]
  );

  const handleDeleteAssignment = useCallback(
    (assignmentId: string) => {
      deleteAssignment.mutate(assignmentId);
    },
    [deleteAssignment]
  );

  if (assignmentsLoading || membersLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-gray-200 animate-pulse rounded" />
        <div className="h-20 bg-gray-100 animate-pulse rounded" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with overallocation warning */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Assigned Resources
        </h3>
        <Popover open={isAddingAssignment} onOpenChange={setIsAddingAssignment}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Assign
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              <h4 className="font-medium">Assign Resource</h4>

              <div className="space-y-2">
                <Label>Team Member</Label>
                <Select
                  value={selectedUserId}
                  onValueChange={setSelectedUserId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMembers?.map((member) => (
                      <SelectItem key={member.user.id} value={member.user.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={member.user.profilePicture || undefined} />
                            <AvatarFallback>
                              {getInitials(member.user.firstName, member.user.lastName, member.user.email)}
                            </AvatarFallback>
                          </Avatar>
                          <span>
                            {member.user.firstName && member.user.lastName
                              ? `${member.user.firstName} ${member.user.lastName}`
                              : member.user.email}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                    {availableMembers?.length === 0 && (
                      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                        No more team members to assign
                      </div>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Units (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={units}
                    onChange={(e) => setUnits(e.target.value)}
                    placeholder="100"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Work (hours)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={work}
                    onChange={(e) => setWork(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsAddingAssignment(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddAssignment}
                  disabled={!selectedUserId || createAssignment.isPending}
                >
                  {createAssignment.isPending ? 'Assigning...' : 'Assign'}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Overallocation Warning */}
      {isOverallocated && (
        <div className="flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>
            Total allocation is {totalUnits}%. Resources may be overallocated.
          </span>
        </div>
      )}

      {/* Assignments List */}
      {assignments && assignments.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center border rounded-lg border-dashed">
          No resources assigned to this task.
        </p>
      ) : (
        <div className="space-y-2">
          {assignments?.map((assignment) => (
            <AssignmentItem
              key={assignment.id}
              assignment={assignment}
              onUpdateUnits={(newUnits) => handleUpdateUnits(assignment.id, newUnits)}
              onUpdateWork={(newWork) => handleUpdateWork(assignment.id, newWork)}
              onDelete={() => handleDeleteAssignment(assignment.id)}
            />
          ))}
        </div>
      )}

      {/* Total Summary */}
      {assignments && assignments.length > 0 && (
        <div className="pt-4 border-t">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total Allocation</span>
            <span className={`font-medium ${isOverallocated ? 'text-yellow-600' : ''}`}>
              {totalUnits}%
            </span>
          </div>
          <div className="flex items-center justify-between text-sm mt-1">
            <span className="text-muted-foreground">Total Work</span>
            <span className="font-medium">
              {assignments.reduce((sum, a) => sum + a.work, 0)}h
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

interface AssignmentItemProps {
  assignment: {
    id: string;
    units: number;
    work: number;
    workUnit: string;
    user?: {
      id: string;
      email: string;
      firstName: string | null;
      lastName: string | null;
      profilePicture: string | null;
    };
  };
  onUpdateUnits: (units: number) => void;
  onUpdateWork: (work: number) => void;
  onDelete: () => void;
}

function AssignmentItem({
  assignment,
  onUpdateUnits,
  onUpdateWork,
  onDelete,
}: AssignmentItemProps) {
  const [editingField, setEditingField] = useState<'units' | 'work' | null>(null);
  const [unitsValue, setUnitsValue] = useState(assignment.units.toString());
  const [workValue, setWorkValue] = useState(assignment.work.toString());

  const handleUnitsBlur = () => {
    setEditingField(null);
    const newUnits = parseInt(unitsValue, 10);
    if (!isNaN(newUnits) && newUnits !== assignment.units) {
      onUpdateUnits(Math.max(0, Math.min(100, newUnits)));
    } else {
      setUnitsValue(assignment.units.toString());
    }
  };

  const handleWorkBlur = () => {
    setEditingField(null);
    const newWork = parseFloat(workValue);
    if (!isNaN(newWork) && newWork !== assignment.work) {
      onUpdateWork(Math.max(0, newWork));
    } else {
      setWorkValue(assignment.work.toString());
    }
  };

  const user = assignment.user;
  const displayName = user
    ? user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.email
    : 'Unknown User';

  return (
    <div className="flex items-center gap-3 p-3 border rounded-lg bg-gray-50">
      <Avatar className="h-10 w-10">
        <AvatarImage src={user?.profilePicture || undefined} />
        <AvatarFallback>
          {getInitials(user?.firstName, user?.lastName, user?.email)}
        </AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{displayName}</p>
        {user?.email && user.firstName && (
          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
        )}
      </div>

      <div className="flex items-center gap-3 shrink-0">
        {/* Units */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Units</p>
          {editingField === 'units' ? (
            <Input
              type="number"
              min="0"
              max="100"
              value={unitsValue}
              onChange={(e) => setUnitsValue(e.target.value)}
              onBlur={handleUnitsBlur}
              onKeyDown={(e) => e.key === 'Enter' && handleUnitsBlur()}
              className="w-16 h-7 text-center"
              autoFocus
            />
          ) : (
            <button
              onClick={() => {
                setUnitsValue(assignment.units.toString());
                setEditingField('units');
              }}
              className="text-sm font-medium px-2 py-1 rounded hover:bg-gray-200"
            >
              {assignment.units}%
            </button>
          )}
        </div>

        {/* Work */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground mb-1">Work</p>
          {editingField === 'work' ? (
            <Input
              type="number"
              min="0"
              step="0.5"
              value={workValue}
              onChange={(e) => setWorkValue(e.target.value)}
              onBlur={handleWorkBlur}
              onKeyDown={(e) => e.key === 'Enter' && handleWorkBlur()}
              className="w-16 h-7 text-center"
              autoFocus
            />
          ) : (
            <button
              onClick={() => {
                setWorkValue(assignment.work.toString());
                setEditingField('work');
              }}
              className="text-sm font-medium px-2 py-1 rounded hover:bg-gray-200"
            >
              {assignment.work}h
            </button>
          )}
        </div>

        {/* Delete */}
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Assignment</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove {displayName} from this task?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function getInitials(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
  email: string | undefined
): string {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase();
  }
  if (email) {
    return email[0].toUpperCase();
  }
  return '?';
}

export default TaskAssignments;
